// src/analytics/hooks.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackSectionView, trackScrollDepth, trackTimeOnPage, trackPageView } from './events';

export function useSectionView(sectionName) {
  const ref = useRef(null);
  const firedRef = useRef(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || firedRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !firedRef.current) {
          firedRef.current = true;
          trackSectionView({ section: sectionName });
          observer.disconnect();
        }
      }
    }, { threshold: [0, 0.5, 1] });
    observer.observe(node);
    return () => observer.disconnect();
  }, [sectionName]);
  return ref;
}

export function useScrollDepth() {
  const firedRef = useRef(new Set());
  const location = useLocation();
  useEffect(() => {
    firedRef.current = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;
      const percent = Math.round((scrollTop / scrollHeight) * 100);
      for (const threshold of [25, 50, 75, 90]) {
        if (percent >= threshold && !firedRef.current.has(threshold)) {
          firedRef.current.add(threshold);
          trackScrollDepth({ percent: threshold });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);
}

export function useTimeOnPage() {
  const location = useLocation();
  useEffect(() => {
    const timers = [
      setTimeout(() => trackTimeOnPage({ seconds: 30 }), 30 * 1000),
      setTimeout(() => trackTimeOnPage({ seconds: 60 }), 60 * 1000),
      setTimeout(() => trackTimeOnPage({ seconds: 120 }), 120 * 1000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [location.pathname]);
}

export function usePageView() {
  const location = useLocation();
  useEffect(() => {
    trackPageView({ page_path: location.pathname, page_title: document.title });
  }, [location.pathname, location.search]);
}
